// Profile Completion Status Utility
// Checks required fields and calculates completion percentage for admin review

export interface ProfileCompletionData {
  profile: {
    full_name?: string | null;
    bio?: string | null;
    gender?: string | null;
    age_range?: string | null;
    referred_by?: string | null;
    is_published?: boolean;
    updated_at?: string | null;
    created_at?: string | null;
  };
  privateData?: {
    email?: string | null;
    phone?: string | null;
    linkedin_url?: string | null;
    country?: string | null;
    updated_at?: string | null;
  } | null;
}

export interface RequiredField {
  key: string;
  label: string;
  source: 'profile' | 'private';
}

// Required fields for profile completion (8 fields — matches profile_required_fields table)
export const REQUIRED_FIELDS: RequiredField[] = [
  { key: 'full_name', label: 'Full Name', source: 'profile' },
  { key: 'email', label: 'Email', source: 'private' },
  { key: 'country', label: 'Country', source: 'private' },
  { key: 'phone', label: 'Phone Number', source: 'private' },
  { key: 'linkedin_url', label: 'LinkedIn URL', source: 'private' },
  { key: 'gender', label: 'Gender', source: 'profile' },
  { key: 'age_range', label: 'Age Range', source: 'profile' },
  { key: 'referred_by', label: 'Referred By', source: 'profile' },
];

export type ProfileCompletionStatus = 'incomplete' | 'pending_approval';

export interface ProfileCompletionResult {
  status: ProfileCompletionStatus;
  statusLabel: string;
  completedCount: number;
  totalCount: number;
  missingFields: RequiredField[];
  completedFields: RequiredField[];
  isComplete: boolean;
}

/**
 * Check if a field value is considered "filled"
 */
function isFieldFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

/**
 * Get the value of a field from profile data
 */
function getFieldValue(
  data: ProfileCompletionData,
  field: RequiredField
): unknown {
  if (field.source === 'profile') {
    return data.profile[field.key as keyof typeof data.profile];
  } else {
    return data.privateData?.[field.key as keyof NonNullable<typeof data.privateData>];
  }
}

/**
 * Calculate profile completion status
 * 
 * @param data - Profile and private data
 * @returns Completion result with status, counts, and missing fields
 */
export function getProfileCompletionStatus(
  data: ProfileCompletionData
): ProfileCompletionResult {
  const missingFields: RequiredField[] = [];
  const completedFields: RequiredField[] = [];

  for (const field of REQUIRED_FIELDS) {
    const value = getFieldValue(data, field);
    if (isFieldFilled(value)) {
      completedFields.push(field);
    } else {
      missingFields.push(field);
    }
  }

  const isComplete = missingFields.length === 0;
  const status: ProfileCompletionStatus = isComplete ? 'pending_approval' : 'incomplete';
  
  return {
    status,
    statusLabel: isComplete ? 'Pending Approval' : 'Profile Incomplete',
    completedCount: completedFields.length,
    totalCount: REQUIRED_FIELDS.length,
    missingFields,
    completedFields,
    isComplete,
  };
}

/**
 * Get relative time string from a date
 * 
 * @param date - ISO date string or Date object
 * @returns Relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function getRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return 'Never updated';
  
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (months > 0) {
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  if (weeks > 0) {
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  if (days > 0) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  if (hours > 0) {
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }
  if (minutes > 0) {
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  }
  return 'Just now';
}

/**
 * Get the most recent activity date from profile data
 */
export function getLastActivityDate(data: ProfileCompletionData): string | null {
  const profileUpdated = data.profile.updated_at;
  const privateUpdated = data.privateData?.updated_at;
  
  if (!profileUpdated && !privateUpdated) {
    return data.profile.created_at || null;
  }
  
  if (!profileUpdated) return privateUpdated || null;
  if (!privateUpdated) return profileUpdated;
  
  return new Date(profileUpdated) > new Date(privateUpdated) 
    ? profileUpdated 
    : privateUpdated;
}
